
































































import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import iwDuration from "@/optimizedEncoding/components/packSelection/Duration.vue";
import iwPricingPack from "@/optimizedEncoding/components/packSelection/PackPricing.vue";
import iwResultsGuaranteeNotice from "@/shared/components/pricing/ResultsGuaranteeNotice.vue";
import iwButtonLoading from "@/shared/components/ButtonLoading.vue";
import iwPackInfoModal from "@/shared/components/pricing/PackInfoModal.vue";
import { getPricing } from "@/shared/scripts/PricingApi";
import { MyClassifiedsStoreState } from "@/shared/interfaces/MyClassifieds.interface";
import { isEmpty } from "lodash";
import { configurePacksOnInit } from "../scripts/packConfig";
import { OptimizedPack } from "@/shared/interfaces/optimizedEncoding/PackSelection.interface";
import { pushPageViewEvent } from "@/shared/store/modules/gtm/datalayer";

interface BillboardPopoverState {
    activeId: string | null;
}

const billboardPopoverState: BillboardPopoverState = { activeId: null };

export default Vue.extend({
    components: {
        iwResultsGuaranteeNotice: iwResultsGuaranteeNotice,
        iwDuration: iwDuration,
        iwPricingPack: iwPricingPack,
        iwButtonLoading: iwButtonLoading,
        iwPackInfoModal: iwPackInfoModal
    },
    props: {
        isNextSectionVisible: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data: function() {
        return {
            packsLoaded: false,
            pathInStore: "transaction.pricing",
            selectedPackageInStore: "publication.visualizationOption",
            configuredPacksInStore: "transaction.configuredPacks",
            checkboxResetVersion: 0,
            packInfoType: null as string | null,
            billboardPopoverState: billboardPopoverState
        };
    },
    provide: function() {
        return {
            billboardPopoverState: billboardPopoverState
        };
    },
    computed: {
        ...mapGetters({
            getValue: "FormModule/getValue",
            isLogged: "UserModule/isLogged",
            package: "OrdersModule/package",
            getValueTransaction: "FormModule/getValueTransaction",
        }),
        transactionType: function(): string {
            return this.getTransactionType();
        },
        shouldReloadPacks: function(): boolean {
            return this.$store.getters["FormModule/getValueUI"]("shouldReloadPacks");
        },
        ...mapState("MyClassifiedsModule", {
            classified: state => (state as MyClassifiedsStoreState).myClassifiedSelected
        }),
        packs: function(): Array<OptimizedPack> {
            if (this.packsLoaded) {
                return this.$store.getters["FormModule/getValueTransaction"](this.configuredPacksInStore);
            }
            return [];
        },
        getSelectedPackage: function(): string {
            return this.$store.getters["FormModule/getValueTransaction"](this.selectedPackageInStore);
        },
        extensionOption: function(): any {
            return this.$store.getters["FormModule/getValueTransaction"]("transaction.pricing.extensionOptionDetails") || {};
        },

        automaticDiscount: function(): { id: string; value: number } | null {
            const discount = this.$store.getters["FormModule/getValueUI"]("transaction.pack.automaticDiscount");
            if (discount && discount.value) {
                return discount;
            }
            return null;
        },
        discountBannerText: function(): string {
            if (!this.automaticDiscount) return "";
            const campaignKey = this.automaticDiscount.id.toLowerCase() + "_title";
            const params = { value: Math.abs(this.automaticDiscount.value) };
            if (this.$te(campaignKey)) {
                return this.$t(campaignKey, params) as string;
            }
            return this.$t("automatic_discount_banner", params) as string;
        },

        billboardOption: function(): any {
            return this.$store.getters["FormModule/getValueTransaction"]("transaction.pricing.billboard") || {};
        },

        isExtensionSelected: function(): boolean {
            const selectedPack = this.getSelectedPackage;
            if (!selectedPack) return false;

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};

            return !!packOptions[selectedPack]?.extension;
        },

        isBillboardSelected: function(): boolean {
            const selectedPack = this.getSelectedPackage;
            if (!selectedPack) return false;

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};

            return !!packOptions[selectedPack]?.billboard;
        },

        selectedPack: function(): any {
            return this.$store.getters["FormModule/getValueTransaction"]("optimizedEncoding.selectedPack") || {};
        },
        totalPrice: function(): string {
            const selectedPack = this.getSelectedPackage;
            if (!selectedPack) return "0";

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};
            const extension = packOptions[selectedPack]?.extension;
            const billboardPrice = Number(packOptions[selectedPack]?.billboard?.price || 0);

            let packPrice;

            // When extension is selected and has per-pack pricing, use the alternative tariff's price
            const extVizOption = extension?.visualizationOptions?.[selectedPack];
            if (extVizOption) {
                packPrice = Number(extVizOption.automaticDiscount != null ? extVizOption.totalPrice : extVizOption.price);
            } else {
                // No extension or no per-pack data: use stored pack price + flat extension fee
                packPrice = Number(
                    this.$store.getters["FormModule/getValueTransaction"](
                        `optimizedEncoding.selectedPack.packPrice.${selectedPack}`
                    ) || 0
                ) + Number(extension?.price || 0);
            }

            const totalPrice = packPrice + billboardPrice;

            return this.formatCurrency(totalPrice.toString());
        }
    },
    watch: {
        getSelectedPackage: function(newPack) {
            if (!newPack) return;

            const packOptions =
                this.getValueTransaction("optimizedEncoding.selectedPackOptions") || {};

            if (packOptions[newPack]) {
                const extension = packOptions[newPack].extension ?? null;

                this.$store.commit("FormModule/addValueTransaction", {
                    path: "transaction.pricing.extensionOption",
                    value: extension
                });

                this.$store.commit(
                    "OrdersModule/setExtensionOption",
                    extension
                );
            }
        },
        shouldReloadPacks: function(newValue: boolean) {
            if (newValue) {
                this.$emit("section-uncompleted");
            }
        },
    },
    created: function() {
        pushPageViewEvent("classified_deposit_v2_step5_package_selection");
        if(!this.packs.length) {
            this.getPacks();
        }
    },
    methods: {
        ...mapActions({
            selectPropertyType: "FormModule/setPropertyType",
        }),
        getPacks: async function(): Promise<Array<OptimizedPack>|null> {
            // Only price the classified currently in the funnel. `myClassifiedSelected` can hold a
            // stale classified from a previous publication/edit in the same session; if its id does
            // not match the funnel's current classified, ignore it and let getPricing read the live
            // form store (correct price + id). Otherwise getPricing would price the stale classified
            // and pick a tariff/discount for the wrong price range (RESTRICTED_TARIFF_PRICE_RANGE).
            const currentClassifiedId = this.$store.getters["FormModule/getClassifiedId"];
            const selectedClassified = (!isEmpty(this.classified) && this.classified.id === currentClassifiedId)
                ? this.classified
                : null;

            return await getPricing({
                classified: selectedClassified,
                type: "PUBLICATION"
            }).then(() =>  {
                const storedPacks = this.$store.getters["FormModule/getValueTransaction"](this.pathInStore);

                if (storedPacks) {
                    const configuredPacks = configurePacksOnInit(storedPacks, this.transactionType);

                    this.$store.commit("FormModule/addValueUI", { "path": "shouldReloadPacks", "value": false });
                    this.$store.commit("FormModule/addValueTransaction", {"path": this.configuredPacksInStore, "value": configuredPacks});
                    this.packsLoaded = true;
                    return this.packs;
                }
                return null;
            })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        },
        openPackInfoModal: function(packType: string) {
            this.packInfoType = packType;
        },
        closePackInfoModal: function() {
            this.packInfoType = null;
        },
        validateAndContinue: function() {
            if (this.getSelectedPackage) {
                this.$emit("section-completed");
            }
        }
    },
});
